// LYDMP（Layer Dump）模块：
// 负责分包请求、分包收集、整包合并以及超时等待管理。
export function createLayerDumpModule(options) {
    const {
        deviceAdapter,
        sendReport,
        reportId,
        createDataHeader,
        sendReportTime,
        getNowTime,
        layerDumpChunks,
        layerDumpData,
        layerDumpWaiters,
        LAYER_DUMP_CHUNK_COUNT,
        LAYER_DUMP_CHUNK_SIZE,
        LAYER_DUMP_TOTAL_SIZE
    } = options

    // 当某层的所有 chunk 到齐后，按固定 chunk 大小拼接成完整层数据。
    function mergeLayerDumpChunks(layer) {
        const layerChunks = layerDumpChunks.value.get(layer)
        if (!layerChunks) return null

        for (let chunk = 0; chunk < LAYER_DUMP_CHUNK_COUNT; chunk++) {
            if (!layerChunks.has(chunk)) return null
        }

        const merged = new Uint8Array(LAYER_DUMP_TOTAL_SIZE)
        for (let chunk = 0; chunk < LAYER_DUMP_CHUNK_COUNT; chunk++) {
            const part = layerChunks.get(chunk)
            const offset = chunk * LAYER_DUMP_CHUNK_SIZE
            merged.set(part.slice(0, LAYER_DUMP_CHUNK_SIZE), offset)
        }
        return merged
    }

    // 结束该层所有等待者（通常是 requestLayerDump 发起的 Promise）。
    function resolveLayerDumpWaiters(layer, mergedData) {
        const waiters = layerDumpWaiters.value.get(layer)
        if (!waiters?.length) return

        layerDumpWaiters.value.delete(layer)
        waiters.forEach(({ resolve, timer }) => {
            clearTimeout(timer)
            resolve(mergedData)
        })
    }

    // 处理单个 chunk 到达：写入缓存，若整层已完整则触发合并与回调。
    function handleLayerDumpChunk(layer, chunk, payload) {
        if (layer < 0 || chunk < 0 || chunk >= LAYER_DUMP_CHUNK_COUNT) return
        if (!layerDumpChunks.value.has(layer)) {
            layerDumpChunks.value.set(layer, new Map())
        }
        const layerChunks = layerDumpChunks.value.get(layer)
        layerChunks.set(chunk, payload)

        const mergedLayerData = mergeLayerDumpChunks(layer)
        if (mergedLayerData) {
            layerDumpData.value.set(layer, mergedLayerData)
            resolveLayerDumpWaiters(layer, mergedLayerData)
        }
    }

    // 发送指定层、指定 chunk 的 LYDMP 请求包。
    async function sendLayerData(layer, chunk) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x4c, 0x59, 0x44, 0x4d, 0x50, layer, chunk]
        const header = createDataHeader(headerIdentifier, 0)
        await sendReport(reportId, header)
        sendReportTime.value = getNowTime()
    }

    // 请求整层数据：
    // 1) 初始化该层分包缓存
    // 2) 注册超时等待者
    // 3) 逐 chunk 发送请求
    // 4) 等待 oninputreport 侧调用 handleLayerDumpChunk 后 resolve
    async function requestLayerDump(layer = 0, timeout = 3000) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }

        const normalizedLayer = Number(layer) || 0
        layerDumpChunks.value.set(normalizedLayer, new Map())

        const waitPromise = new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                const currentWaiters = layerDumpWaiters.value.get(normalizedLayer) || []
                const nextWaiters = currentWaiters.filter(item => item.resolve !== resolve)
                if (nextWaiters.length > 0) {
                    layerDumpWaiters.value.set(normalizedLayer, nextWaiters)
                } else {
                    layerDumpWaiters.value.delete(normalizedLayer)
                }
                reject(new Error(`读取 layer ${normalizedLayer} 超时`))
            }, timeout)

            const currentWaiters = layerDumpWaiters.value.get(normalizedLayer) || []
            currentWaiters.push({ resolve, reject, timer })
            layerDumpWaiters.value.set(normalizedLayer, currentWaiters)
        })

        for (let chunk = 0; chunk < LAYER_DUMP_CHUNK_COUNT; chunk++) {
            await sendLayerData(normalizedLayer, chunk)
        }

        return await waitPromise
    }

    return {
        mergeLayerDumpChunks,
        resolveLayerDumpWaiters,
        handleLayerDumpChunk,
        sendLayerData,
        requestLayerDump
    }
}

